/**
 * Computes the new full assignment list for the current environment after a user
 * changes a document's site selection in the ICFs view.
 *
 * `current` must already be scoped to the current environment. Assignments for this
 * document to sites **outside** the visible environment (none, in practice, since
 * `current` is per-environment) are preserved defensively.
 */
export function replaceLinksForDocument(current, consentDocumentId, selectedEnvironmentSiteIds, environmentSiteIds) {
    const scope = new Set(environmentSiteIds);
    const selected = new Set(selectedEnvironmentSiteIds);
    const kept = current.filter((a) => {
        if (a.consentDocumentId !== consentDocumentId) {
            return true;
        }
        if (!scope.has(a.environmentSiteId)) {
            return true;
        }
        return selected.has(a.environmentSiteId);
    });
    const existingSiteIds = new Set(kept.filter((a) => a.consentDocumentId === consentDocumentId).map((a) => a.environmentSiteId));
    const additions = [];
    for (const environmentSiteId of selectedEnvironmentSiteIds) {
        if (!existingSiteIds.has(environmentSiteId)) {
            additions.push({
                id: `pending:${environmentSiteId}:${consentDocumentId}`,
                consentDocumentId,
                environmentSiteId,
            });
        }
    }
    return [...kept, ...additions];
}
/**
 * Computes the new full assignment list for the current environment after a user
 * changes the document selection for one site in the Sites view.
 *
 * Links to documents **outside** `consentDocumentIdsInScope` (e.g. stale/archived ids
 * that may still exist in state) are preserved so we don't blow them away.
 */
export function replaceLinksForSite(current, environmentSiteId, selectedConsentDocumentIds, consentDocumentIdsInScope) {
    const scope = new Set(consentDocumentIdsInScope);
    const selected = new Set(selectedConsentDocumentIds);
    const kept = current.filter((a) => {
        if (a.environmentSiteId !== environmentSiteId) {
            return true;
        }
        if (!scope.has(a.consentDocumentId)) {
            return true;
        }
        return selected.has(a.consentDocumentId);
    });
    const existingDocIds = new Set(kept.filter((a) => a.environmentSiteId === environmentSiteId).map((a) => a.consentDocumentId));
    const additions = [];
    for (const consentDocumentId of selectedConsentDocumentIds) {
        if (!existingDocIds.has(consentDocumentId)) {
            additions.push({
                id: `pending:${environmentSiteId}:${consentDocumentId}`,
                consentDocumentId,
                environmentSiteId,
            });
        }
    }
    return [...kept, ...additions];
}
